import { z } from 'zod';
import { AccountIdSchema } from '../ids.js';
import {
  AwsConnectionInputSchema,
  AwsConnectionSchema,
  CodeDetailSchema,
  CodeRunSchema,
  CodeStartSchema,
  CodeWorkspaceSchema,
} from '../rumi-code.js';
import { defineRoute } from './common.js';
const account = z.object({ accountId: AccountIdSchema });
const run = account.extend({ runId: z.string().uuid() });
const empty = z.object({}).strict();
const base = '/v1/accounts/:accountId/rumi-code';
export const CodeApprovalSchema = z.object({ digest: z.string().regex(/^[a-f0-9]{64}$/), approve: z.boolean() }).strict();
export const CodeProviderResultSchema = z.object({
  status: z.string().max(2000),
  evidenceId: z.string().max(1000).nullable(),
  kind: z.enum(['recheck', 'upload']),
});
export const rumiCodeRoutes = {
  'rumicode.workspace': defineRoute({
    id: 'rumicode.workspace',
    method: 'GET',
    path: base,
    auth: 'desktop',
    capability: 'rumi_code.read',
    paramsSchema: account,
    requestSchema: empty,
    responseSchema: CodeWorkspaceSchema,
    noBodyLog: true,
    description: 'AWS remediation workspace and recent runs.',
  }),
  'rumicode.prepare': defineRoute({
    id: 'rumicode.prepare',
    method: 'POST',
    path: `${base}/connection/prepare`,
    auth: 'desktop',
    capability: 'rumi_code.configure',
    paramsSchema: account,
    requestSchema: empty,
    responseSchema: AwsConnectionSchema,
    noBodyLog: true,
    description: 'Create the customer-specific AWS external ID.',
  }),
  'rumicode.configure': defineRoute({
    id: 'rumicode.configure',
    method: 'PUT',
    path: `${base}/connection`,
    auth: 'desktop',
    capability: 'rumi_code.configure',
    paramsSchema: account,
    requestSchema: AwsConnectionInputSchema,
    responseSchema: AwsConnectionSchema,
    noBodyLog: true,
    description: 'Save scoped AWS role, regions, authorized operators and preauthorization.',
  }),
  'rumicode.testConnection': defineRoute({
    id: 'rumicode.testConnection',
    method: 'POST',
    path: `${base}/connection/test`,
    auth: 'desktop',
    capability: 'rumi_code.configure',
    paramsSchema: account,
    requestSchema: empty,
    responseSchema: AwsConnectionSchema,
    noBodyLog: true,
    description: 'Assume the role and verify the AWS account identity.',
  }),
  'rumicode.iamPolicy': defineRoute({
    id: 'rumicode.iamPolicy',
    method: 'GET',
    path: `${base}/connection/policy`,
    auth: 'desktop',
    capability: 'rumi_code.configure',
    paramsSchema: account,
    requestSchema: empty,
    responseSchema: z.object({ trust: z.json(), permissions: z.json(), instructions: z.string() }),
    noBodyLog: true,
    description: 'Generate scoped customer IAM policies for this connection.',
  }),
  'rumicode.start': defineRoute({
    id: 'rumicode.start',
    method: 'POST',
    path: `${base}/runs`,
    auth: 'desktop',
    capability: 'rumi_code.run',
    paramsSchema: account,
    requestSchema: CodeStartSchema,
    responseSchema: CodeRunSchema,
    noBodyLog: true,
    description: 'Queue a remediation for a selected provider test.',
  }),
  'rumicode.detail': defineRoute({
    id: 'rumicode.detail',
    method: 'GET',
    path: `${base}/runs/:runId`,
    auth: 'desktop',
    capability: 'rumi_code.read',
    paramsSchema: run,
    requestSchema: empty,
    responseSchema: CodeDetailSchema,
    noBodyLog: true,
    description: 'Read durable progress and before/after evidence.',
  }),
  'rumicode.approve': defineRoute({
    id: 'rumicode.approve',
    method: 'POST',
    path: `${base}/runs/:runId/approval`,
    auth: 'desktop',
    capability: 'rumi_code.approve',
    paramsSchema: run,
    requestSchema: CodeApprovalSchema,
    responseSchema: CodeRunSchema,
    noBodyLog: true,
    description: 'Approve or reject the exact hashed remediation plan.',
  }),
  'rumicode.cancel': defineRoute({
    id: 'rumicode.cancel',
    method: 'POST',
    path: `${base}/runs/:runId/cancel`,
    auth: 'desktop',
    capability: 'rumi_code.run',
    paramsSchema: run,
    requestSchema: empty,
    responseSchema: CodeRunSchema,
    noBodyLog: true,
    description: 'Stop before the next change; in-flight changes remain verifiable.',
  }),
  'rumicode.providerResult': defineRoute({
    id: 'rumicode.providerResult',
    method: 'POST',
    path: `${base}/runs/:runId/provider`,
    auth: 'desktop',
    capability: 'rumi_code.run',
    paramsSchema: run,
    requestSchema: CodeProviderResultSchema,
    responseSchema: CodeRunSchema,
    noBodyLog: true,
    description: 'Record the separately observed provider recheck or evidence upload result.',
  }),
} as const;
